// ─────────────────────────────────────────────────────────────────────────────
// REQ-111 — Formulario de Nominación de "Brillamos Juntos" (/app/nominar).
//
// Wizard de 3 campos (compañero · fundamental · qué hizo) con PREVIEW en vivo tipo post-it,
// nudge de mensaje ("Enviar así" / "Agregar línea"), pantalla de éxito con redirect a /app/brillamos
// (3.2 s) y preselección de fundamental por query `?fund=ID`. Persiste vía API REAL
// (useCreateRecognition contra la campaña activa). El scoring es server-side; el front solo envía.
// SIN puntos. CERO mocks.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useRecognitionCatalog,
  useActiveCampaign,
  useCreateRecognition,
  usePeopleSearch,
  getFundamentalName,
  getFundamentalColor,
  getBehaviorName,
  getHabitName,
  availableHabitsFor,
  MENSAJE_MAX_LENGTH,
} from '@/api/recognition';
import type { PersonSummary, FundamentalDto, CreateRecognitionBody } from '@/api/recognition';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { useUserContext } from '@/contexts/UserContext';
import { EmojiPickerButton } from '@/components/common/EmojiPickerButton';
import { InitialsAvatar } from '@/components/common';
import { insertAtCursor } from '@/utils/insertAtCursor';
import { RecognitionShell, StateBlock } from './components';
import { MessageLimitHint, recognitionSendErrorMessage } from './messageLimit';
import { useAutoGrowTextarea } from '@/hooks/useAutoGrowTextarea';
import { StreakWeekTracker } from './StreakTracker';
import { getFundamentalIcon } from './fundamentalAssets';

const REDIRECT_MS = 3200;

/** Debounce simple. */
function useDebounced(value: string, delay = 250): string {
  const [d, setD] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setD(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return d;
}

/** Una "acción" del banco: puede ser una actitud (F01) o un daily habit. */
interface Action {
  id: string;
  name: string;
  kind: 'behavior' | 'habit';
}

function NominarContent() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { demoUser } = useUserContext();

  const catalog = useRecognitionCatalog();
  const campaign = useActiveCampaign(demoUser.ou || undefined);
  const create = useCreateRecognition();

  const funds: FundamentalDto[] = useMemo(
    () => (catalog.data ?? []).filter((f) => f.isActive),
    [catalog.data],
  );

  const [nominee, setNominee] = useState<PersonSummary | null>(null);
  const [fundId, setFundId] = useState<string | null>(searchParams.get('fund'));
  const [action, setAction] = useState<Action | null>(null);
  const [message, setMessage] = useState('');
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [sent, setSent] = useState(false);
  // Guarda de cambios sin guardar: la nominación está "sucia" si el usuario empezó a llenarla
  // (nominado, fundamental, acción o mensaje) y aún no la envió.
  const hasUnsavedDraft = !sent && (
    message.trim().length > 0 || nominee !== null || fundId !== null || action !== null
  );
  useUnsavedChangesGuard(hasUnsavedDraft);

  // Búsqueda de compañero.
  const [raw, setRaw] = useState('');
  const search = useDebounced(raw);
  const people = usePeopleSearch({ search, pageSize: 6 });
  const options = (people.data?.items ?? []).filter((p) => p.id !== demoUser.userId);

  const fund = funds.find((f) => f.id === fundId) ?? null;
  const fundIdx = fund ? funds.indexOf(fund) : 0;
  const fundColor = fund ? getFundamentalColor(fund, fundIdx) : 'var(--cp-purple)';
  // H-70 (firma del post-it): ¿cruza OUs? Mismo criterio que BrillamosJuntosPage.tsx del demo.
  // Se compara NOMBRE contra NOMBRE: `demoUser.ou` es la clave de la OU, no su etiqueta (P3-2).
  const isCrossOu = !!(nominee?.ouName && demoUser.ouNombre && nominee.ouName !== demoUser.ouNombre);

  // Banco de acciones del fundamental elegido: actitudes (F01) + daily habits.
  const actions: Action[] = useMemo(() => {
    if (!fund) return [];
    const behaviors = (fund.attitudes ?? [])
      .filter((a) => a.isActive)
      .map<Action>((a) => ({ id: a.id, name: getBehaviorName(a, lang), kind: 'behavior' }));
    const habits = availableHabitsFor(fund, (fund.attitudes ?? []).map((a) => a.id))
      .map<Action>((h) => ({ id: h.id, name: getHabitName(h, lang), kind: 'habit' }));
    return [...behaviors, ...habits];
  }, [fund, lang]);

  // Si cambia el fundamental, la acción previa deja de ser válida.
  useEffect(() => {
    if (action && !actions.some((a) => a.id === action.id)) setAction(null);
  }, [actions, action]);

  const done = [nominee, fundId, action].filter(Boolean).length;
  const canProceed = done === 3;

  const buildSentence = () => [
    nominee ? t('nominate.thanksTo', { name: nominee.displayName ?? nominee.email }) : '',
    action ? action.name.toLowerCase() : '',
    fund ? t('nominate.demonstrating', { fundamental: getFundamentalName(fund, lang) }) : '',
  ].filter(Boolean).join(' ');

  const doSend = (msg: string) => {
    if (!fund || !nominee) return;
    const body: CreateRecognitionBody = {
      receptorIds: [nominee.id],
      fundamentalIds: [fund.id],
      mensaje: msg,
      behaviorIds: action?.kind === 'behavior' ? [action.id] : [],
      habitIds: action?.kind === 'habit' ? [action.id] : [],
      campanaId: campaign.data?.id ?? null,
    };
    create.mutate(body, {
      onSuccess: () => {
        setSent(true);
        setShowPopup(false);
        setTimeout(() => navigate('/app/brillamos'), REDIRECT_MS);
      },
    });
  };

  // Misma caja que el resto de pantallas: 4 líneas de piso, 10 de techo y scroll a partir de ahí.
  // Va antes del `return` de la pantalla de éxito — más abajo quedaría como hook condicional.
  useAutoGrowTextarea(messageRef, message);

  const handleSend = () => {
    if (!message.trim()) { setNudge(true); return; }
    doSend(message.trim());
  };
  const handleSendAnyway = () => doSend(buildSentence() || t('nominate.defaultMessage'));

  if (sent) {
    // H-70: trofeo real (`sigma-07.png`) en vez del emoji 🏆; `.nom-sent-title`/`-sub` (antes
    // <h2>/<p> sin clase propia).
    return (
      <div className="nom-sent">
        <img src="/brillamos/sigma-07.png" alt="" className="nom-sent-trophy" />
        <h2 className="nom-sent-title">{t('nominate.sentTitle')}</h2>
        <p className="nom-sent-sub">{t('nominate.sentSubtitle', { name: nominee?.displayName ?? nominee?.email ?? '' })}</p>
        <p className="nom-sent-redirect">{t('nominate.redirecting')}</p>
      </div>
    );
  }

  const charLen = message.length;
  const charQuality = charLen === 0 ? '' : charLen < 30 ? 'nom-char--short' : charLen <= 80 ? 'nom-char--ideal' : 'nom-char--long';

  return (
    <div className="nom-page">
      <Link to="/app/brillamos" className="nom-back"><i className="fas fa-chevron-left" /> {t('nominate.back')}</Link>

      {/* RF-041 (D-2) — tracker L–D. FUERA del `StateBlock` a propósito, y no dentro del riel de la
          vista previa: el bloque envuelve el `.nom-layout` entero, así que ahí dentro el tracker
          desaparecía en cuanto el catálogo de Fundamentales estuviera cargando, vacío o en error —
          verificado en local, donde el catálogo vacío dejaba la pantalla sin tracker. La racha no
          depende de ese catálogo. Sujeto al gate de RF-043.
          BUG (racha inconsistente, 2026-08-23) — esta pantalla es SIEMPRE Brillamos Juntos
          (REQ-111), así que la tira debe ser la de ese programa, no la de Fundamentales por
          defecto: mismo criterio que `BrillamosView`/`StreakWidget programa="Brillamos"`. */}
      <StreakWeekTracker programa="Brillamos" />

      <StateBlock
        isLoading={catalog.isLoading}
        isError={catalog.isError}
        error={catalog.error}
        isEmpty={funds.length === 0}
        emptyText={t('catalog.empty')}
        onRetry={() => { catalog.refetch(); }}
      >
        <div className="nom-layout">
          {/* Preview en vivo — `.nom-preview` → `.nom-sidebar` (H-69). */}
          <aside className="nom-sidebar">
            <div className="nom-postit-preview">
              <p className="nom-postit-label">{t('nominate.yourRecognition')}</p>
              <div className="nom-postit-sentence">
                <span className="nom-postit-word">{t('nominate.thanksWord')} </span>
                <span className={`nom-postit-slot ${nominee ? 'nom-postit-slot--filled' : ''}`}>{nominee ? (nominee.displayName ?? nominee.email) : t('nominate.slotWho')}</span>
                <span className="nom-postit-word"> {t('nominate.forWord')} </span>
                <span className={`nom-postit-slot ${action ? 'nom-postit-slot--filled' : ''}`}>{action ? action.name.toLowerCase() : t('nominate.slotWhat')}</span>
                <span className="nom-postit-word"> {t('nominate.demonstratingWord')} </span>
                {/* Color dinámico por Fundamental: el DEMO TAMBIÉN lo deja inline (NominarPage.tsx :
                    121-124, `style={fund ? {color: fund.color, borderBottomColor: fund.color} : {}}`)
                    — no hay clase estática posible para un color de catálogo. Verificado punto por
                    punto contra el código fuente del demo (ver ESTADO.md, decisión F5 §26). */}
                <span className={`nom-postit-slot ${fund ? 'nom-postit-slot--filled' : ''}`} style={fund ? { color: fundColor, borderBottomColor: fundColor } : {}}>
                  {fund ? getFundamentalName(fund, lang) : t('nominate.slotFund')}
                </span>
              </div>

              {/* Firma del post-it (H-70/punto 21) — avatar de iniciales: `PersonSummary` no trae
                  foto (mismo criterio que `.bj-av`, ver brillamosShared.tsx). */}
              <div className="nom-postit-firma">
                {nominee ? (
                  <div className="nom-postit-firma-tag">
                    <InitialsAvatar
                      name={nominee.displayName ?? nominee.email}
                      baseClass="nom-postit-firma-av"
                      seed={nominee.id}
                    />
                    <div className="nom-postit-firma-info">
                      <span className="nom-postit-firma-name">@{nominee.displayName ?? nominee.email}</span>
                      {nominee.jobTitle && <span className="nom-postit-firma-role">{nominee.jobTitle}</span>}
                    </div>
                    {isCrossOu && <span className="nom-postit-firma-cross">✨ {t('feed.crossOu')}</span>}
                  </div>
                ) : (
                  <div className="nom-postit-firma-empty">
                    <span>@{t('nominate.slotWho')}</span>
                  </div>
                )}
              </div>

              <div className="nom-postit-progress">
                <div className="nom-postit-progress-bar"><div className="nom-postit-progress-fill" style={{ width: `${(done / 3) * 100}%` }} /></div>
                <span className="nom-postit-progress-label">{t('nominate.elements', { done })}</span>
              </div>
            </div>
          </aside>

          {/* Formulario */}
          <main className="nom-main nom-step">
            <div className="nom-step-hd">
              <span className="nom-eyebrow">{t('nominate.eyebrow')}</span>
              <h1 className="nom-title">{t('nominate.title')}</h1>
            </div>

            {/* Campo 1 — compañero. H-69/punto 20: autocomplete PROPIO (`.nom-tag-box`/
                `.nom-at-symbol`/`.nom-tag-input`/`.nom-autoc*`), deja de reutilizar
                `.rp-search`/`.rp-autocomplete`. Avatar de iniciales (sin foto real, ver
                brillamosShared.tsx) con las clases del demo `.nom-tag-av`/`.nom-autoc-av`. */}
            <div className="nom-field">
              <label className="nom-field-label"><span className="nom-field-num">1</span> {t('nominate.field1')}</label>
              {nominee ? (
                <div className="nom-tag-selected">
                  <InitialsAvatar
                    name={nominee.displayName ?? nominee.email}
                    baseClass="nom-tag-av"
                    seed={nominee.id}
                  />
                  <div className="nom-tag-info">
                    <span className="nom-tag-handle">{nominee.displayName ?? nominee.email}</span>
                    {nominee.jobTitle && <span className="nom-tag-role">{nominee.jobTitle}</span>}
                  </div>
                  <button type="button" className="nom-chip-rm" onClick={() => setNominee(null)} aria-label={t('common.remove')}><i className="fas fa-xmark" /></button>
                </div>
              ) : (
                <div className="nom-search-wrap">
                  <div className="nom-tag-box">
                    <span className="nom-at-symbol" aria-hidden="true">@</span>
                    <input
                      className="nom-tag-input"
                      value={raw}
                      onChange={(e) => setRaw(e.target.value)}
                      placeholder={t('nominate.searchPlaceholder')}
                      aria-label={t('nominate.field1')}
                      autoFocus
                    />
                  </div>
                  {search.trim().length >= 2 && (
                    options.length > 0 || people.isLoading || people.isError ? (
                      <div className="nom-autoc">
                        {people.isLoading && <div className="nom-autoc-row">{t('common.loading')}</div>}
                        {people.isError && <div className="nom-autoc-row">{t('panel.searchError')}</div>}
                        {!people.isLoading && !people.isError && options.map((p) => (
                          <button key={p.id} type="button" className="nom-autoc-row" onClick={() => { setNominee(p); setRaw(''); }}>
                            <InitialsAvatar
                              name={p.displayName ?? p.email}
                              baseClass="nom-autoc-av"
                              seed={p.id}
                            />
                            <div className="nom-autoc-info">
                              <span className="nom-autoc-name">{p.displayName ?? p.email}</span>
                              {p.jobTitle && <span className="nom-autoc-sub">{p.jobTitle}</span>}
                            </div>
                            {p.ouName && demoUser.ouNombre && p.ouName !== demoUser.ouNombre && <span className="nom-cross-tiny">{t('feed.crossOu')}</span>}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="nom-no-results">{t('nominate.noResults')}</div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Campo 2 — fundamental. H-69/punto 25-26: `.nom-fund-pill` pasa de color LITERAL
                inline (`background:c, borderColor:c, color:'#fff'`) a variable CSS `--bj-acc`
                (mismo patrón que el demo, NominarPage.tsx : 291: `style={{'--bj-acc': f.color}}`)
                consumida por `.nom-fund-pill--on` — deja de duplicar en JS lo que ya resuelve la
                clase. Icono real del Fundamental (`.nom-fund-pill-ico`), antes solo texto.
                NOTA de orden: el demo pone este campo en la posición 3 ("¿en qué Fundamental?")
                porque su banco de acciones (campo 2, "¿qué hizo?") es una lista PLANA de TODAS
                las acciones de TODOS los Fundamentales (sin depender de elegir uno antes). El
                integrado en cambio deriva el banco de acciones EN CASCADA a partir del
                Fundamental elegido (dato real: actitudes/hábitos pertenecen a un Fundamental) —
                es funcionalidad real que no existe en la maqueta y que no se retira (regla 6).
                Se conserva el orden fundamental→acción del integrado; ver decisión F5 en
                ESTADO.md. */}
            <div className="nom-field">
              <label className="nom-field-label"><span className="nom-field-num">2</span> {t('nominate.field2')}</label>
              <div className="nom-fund-row">
                {funds.map((f, idx) => {
                  const on = fundId === f.id;
                  const c = getFundamentalColor(f, idx);
                  return (
                    <button key={f.id} type="button" className={`nom-fund-pill ${on ? 'nom-fund-pill--on' : ''}`}
                      style={{ '--bj-acc': c } as React.CSSProperties}
                      onClick={() => setFundId(on ? null : f.id)}>
                      <img src={getFundamentalIcon(f, idx)} alt="" className="nom-fund-pill-ico" />
                      <span>{getFundamentalName(f, lang)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Campo 3 — qué hizo (H-69/punto 26: `.nom-action-bank`/`-chip` → `.nom-accion-bank`/
                `-chip`). Color dinámico por acción: el DEMO TAMBIÉN lo deja inline
                (NominarPage.tsx : 263-271, `style={{borderColor:a.fundColor, background:...,
                color:a.fundColor}}`) — cada acción pertenece a un Fundamental distinto, no hay
                clase estática posible. Verificado contra el código fuente del demo. */}
            <div className="nom-field">
              <label className="nom-field-label"><span className="nom-field-num">3</span> {t('nominate.field3')}</label>
              {fund ? (
                actions.length > 0 ? (
                  <div className="nom-accion-bank">
                    {actions.map((a) => {
                      const on = action?.id === a.id;
                      return (
                        <button key={a.id} type="button" className={`nom-accion-chip ${on ? 'nom-accion-chip--on' : ''}`}
                          style={on ? { borderColor: fundColor, background: `${fundColor}18`, color: fundColor } : {}}
                          onClick={() => setAction(on ? null : a)}>
                          {a.name}
                        </button>
                      );
                    })}
                  </div>
                ) : <p className="rp-note">{t('nominate.noActions')}</p>
              ) : <p className="rp-note">{t('nominate.pickFundFirst')}</p>}
            </div>

            <div className="nom-nav">
              <button type="button" className={`nom-btn-complete ${!canProceed ? 'nom-btn--off' : ''}`} disabled={!canProceed} onClick={() => { setShowPopup(true); setNudge(false); }}>
                {t('nominate.completeMessage')} <i className="fas fa-arrow-right" />
              </button>
            </div>
          </main>
        </div>
      </StateBlock>

      {/* Popup de mensaje. El overlay era `.nom-overlay`, un renombre de `.rec-modal-overlay`
          para tener namespace propio del demo (H-69). F5 lo dejó sin objeto: los quince overlays
          declaraban lo mismo y ahora comparten la primitiva `.modal-overlay`. */}
      {/* createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
          isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de
          apilamiento — nunca compite contra el z-index explícito del Sidebar, que queda pintado
          encima. Mismo fix ya aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx. */}
      {showPopup && createPortal((
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => { setShowPopup(false); setNudge(false); }}>
          <div className="nom-popup" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="nom-popup-close" onClick={() => { setShowPopup(false); setNudge(false); }} aria-label={t('common.close')}><i className="fas fa-xmark" /></button>
            <div className="nom-popup-hd">
              <img src="/brillamos/sigma-01.png" alt="" className="nom-popup-ico" />
              <div>
                <h2 className="nom-popup-title">{t('nominate.personalizeTitle')}</h2>
                <p className="nom-popup-sub">{t('nominate.personalizeSub')}</p>
              </div>
            </div>
            <div className="nom-popup-sentence">{buildSentence()}</div>

            <div className="nom-popup-field">
              <div className="msg-emoji-row">
                <EmojiPickerButton
                  onSelect={(emoji) => {
                    const { next, cursor } = insertAtCursor(messageRef.current, message, emoji, MENSAJE_MAX_LENGTH);
                    setMessage(next);
                    setNudge(false);
                    requestAnimationFrame(() => {
                      messageRef.current?.focus();
                      messageRef.current?.setSelectionRange(cursor, cursor);
                    });
                  }}
                />
              </div>
              <textarea ref={messageRef} className="nom-textarea msg-autogrow" rows={4}
                maxLength={MENSAJE_MAX_LENGTH} value={message}
                onChange={(event) => { setMessage(event.target.value); setNudge(false); }}
                placeholder={t('nominate.messagePlaceholder')} />
              <MessageLimitHint length={charLen} />
              <div className="nom-char-row">
                <span className={`nom-char ${charQuality}`}>{t('nominate.charCount', { count: charLen })}</span>
                <span className="nom-char-hint">{t('nominate.idealRange')}</span>
              </div>
            </div>

            {create.isError && (
              <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {recognitionSendErrorMessage(create.error, t, t('panel.sendErrorGeneric'))}</p>
            )}

            {nudge ? (
              <div className="nom-nudge">
                <p>{t('nominate.nudgeText')}</p>
                <div className="nom-nudge-actions">
                  <button type="button" className="nom-nudge-add" onClick={() => setNudge(false)}>{t('nominate.addLine')}</button>
                  <button type="button" className="nom-nudge-anyway" onClick={handleSendAnyway} disabled={create.isPending}>{t('nominate.sendAnyway')}</button>
                </div>
              </div>
            ) : (
              <button type="button" className={`nom-btn-send ${create.isPending ? 'nom-btn--off' : ''}`} onClick={handleSend} disabled={create.isPending}>
                <img src="/brillamos/sigma-05.png" alt="" className="nom-btn-send-ico" />
                {create.isPending ? t('panel.sending') : t('nominate.send')}
              </button>
            )}
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

export function NominarView() {
  return (
    <RecognitionShell>
      <NominarContent />
    </RecognitionShell>
  );
}

export default NominarView;
